import * as nodePath from 'node:path';
import { useStore } from '@nanostores/react';
import { memo, useCallback, useMemo, useRef, type FC, type HTMLAttributes } from 'react';
import { FileOrFolder } from './FileOrFolder';
import { FileTree } from './FileTree';
import { SlidePanel, SlidePanelContainer } from './SlidePanel';
import { SearchPanel } from './search-panel/SearchPanel';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { PanelHeader } from '~/components/ui/PanelHeader';
import * as Tooltip from '~/components/ui/Tooltip';
import type { FileChangeCallback } from '~/components/workbench/FileContextMenu';
import { useCollapsedFolders } from '~/lib/hooks/useCollapsedFolders';
import type { FileMap } from '~/lib/stores/files';
import { workbenchStore, type FileTreeViewType } from '~/lib/stores/workbench';
import type { FolderNode, FileNode } from '~/types/file-tree';
import { classNames } from '~/utils/classNames';
import { WORK_DIR } from '~/utils/constants';

interface FileTreePanelProps {
  files: FileMap | undefined;
  unsavedFiles: Set<string> | undefined;
  rootFolder: string;
  selectedFile: string | undefined;
  onFileSelect: ((filePath: string) => void) | undefined;
  onFileChange?: FileChangeCallback;
}

export const FileTreePanel = memo<FileTreePanelProps>(({ unsavedFiles, selectedFile, onFileSelect, ...props }) => {
  const searchPanelRef = useRef<{ focusInput: () => void }>(null);

  const tab = useStore(workbenchStore.currentFileTreeView);
  const lockedFiles = useStore(workbenchStore.lockedFiles);
  const targetedFiles = useStore(workbenchStore.targetedFiles);

  const lockedFileCount = Object.values(lockedFiles).filter((fileOrFolder) => fileOrFolder?.isLocked).length;

  const targetedFileCount = Object.values(targetedFiles).filter((fileOrFolder) => fileOrFolder?.isTargeted).length;

  const foldersContainingLockedOrTargetedFiles = useMemo(() => {
    const folderPaths = new Set<string>();

    for (const filePath of Object.keys(lockedFiles).concat(Object.keys(targetedFiles))) {
      let parentDir = nodePath.dirname(filePath);

      while (parentDir !== WORK_DIR) {
        folderPaths.add(parentDir);
        parentDir = nodePath.dirname(parentDir);
      }
    }

    return folderPaths;
  }, [lockedFiles, targetedFiles]);

  const currentFocusType = targetedFileCount > 0 ? 'targeted' : lockedFileCount > 0 ? 'locked' : 'none';

  const clearFocused = useCallback(
    (file: FileNode | FolderNode) => {
      workbenchStore.removeFromTargetOrLockedFiles(file.fullPath);

      if (lockedFileCount + targetedFileCount === 1) {
        workbenchStore.currentFileTreeView.set('files');
      }
    },
    [lockedFileCount, targetedFileCount],
  );

  const onViewSelect = useCallback((view: FileTreeViewType) => {
    workbenchStore.currentFileTreeView.set(view);
  }, []);

  function provideQuickActions(fileOrFolder: FolderNode | FileNode): JSX.Element {
    const isTarget = targetedFiles[fileOrFolder.fullPath]?.isTargeted;
    const isLocked = lockedFiles[fileOrFolder.fullPath]?.isLocked;

    const isFolderContainingFocused = foldersContainingLockedOrTargetedFiles.has(fileOrFolder.fullPath);

    const isIconVisible = isTarget || isLocked || isFolderContainingFocused;

    const isSelected = fileOrFolder.fullPath === selectedFile;

    const unsavedChanges = unsavedFiles?.has(fileOrFolder.fullPath);

    return (
      <>
        {unsavedChanges && <span className="i-ph:circle-fill scale-68 shrink-0 text-orange-500" />}
        {isIconVisible && (
          <IconElement
            isFileSelected={isSelected}
            isFocused={isTarget || isLocked || isFolderContainingFocused}
            isClickable={isTarget || isLocked}
            className={classNames({
              'i-ph:crosshair': isTarget,
              'i-ph:dot-outline-fill': isFolderContainingFocused,
              'i-ph:lock-simple': isLocked,
            })}
            onClick={(event) => {
              event.stopPropagation();

              if (isTarget || isLocked) {
                workbenchStore.setFileOrFolderTarget(fileOrFolder.fullPath, false);
              }
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col border-r border-bolt-elements-borderColor overflow-hidden h-full">
      <div className="relative h-full grid grid-rows-[auto_1fr] overflow-hidden">
        <FilesHeader
          tab={tab}
          targetedFileCount={targetedFileCount}
          lockedFileCount={lockedFileCount}
          onViewSelect={onViewSelect}
        />

        <SlidePanelContainer value={tab}>
          <SlidePanel name="files">
            <FileTree
              className="flex-grow overflow-y-auto"
              hideRoot
              collapsed
              hasDragDrop
              preserveCollapsedState
              provideQuickActions={provideQuickActions}
              selectedFile={selectedFile}
              onFileSelect={onFileSelect}
              {...props}
            />
          </SlidePanel>
          <SlidePanel name="search" onActivate={() => searchPanelRef.current?.focusInput()}>
            <SearchPanel ref={searchPanelRef} />
          </SlidePanel>
          <SlidePanel name="focused">
            {(lockedFileCount > 0 || targetedFileCount > 0) && (
              <>
                <FocusedFilesHeader type={currentFocusType} onViewSelect={onViewSelect} />
                <FocusedFilesList
                  type={currentFocusType}
                  files={targetedFileCount > 0 ? targetedFiles : lockedFiles}
                  selectedFile={selectedFile}
                  onFileSelect={onFileSelect}
                  clearFocused={clearFocused}
                />
              </>
            )}
          </SlidePanel>
        </SlidePanelContainer>
      </div>
    </div>
  );
});

interface FilesHeaderProps {
  tab: FileTreeViewType;
  targetedFileCount: number;
  lockedFileCount: number;
  onViewSelect: (view: FileTreeViewType) => void;
}

const FilesHeader = memo<FilesHeaderProps>(({ tab, lockedFileCount, targetedFileCount, onViewSelect }) => {
  return (
    <PanelHeader className="px-1.5 gap-1 @container">
      <LegacyIconButton
        className={classNames('gap-1.5 px-1 h-6 @[160px]:px-1.5', {
          'text-bolt-elements-textPrimary': tab === 'files',
        })}
        onClick={() => onViewSelect('files')}
      >
        <div className="i-ph:tree-structure-duotone shrink-0 size-4 @[160px]:hidden @[200px]:block" />
        <span className="hidden @[160px]:block">Files</span>
      </LegacyIconButton>

      <LegacyIconButton
        className={classNames('gap-1.5 px-1 h-6 @[160px]:px-1.5', {
          'text-bolt-elements-textPrimary': tab === 'search',
        })}
        onClick={() => onViewSelect('search')}
      >
        <div className="i-ph:magnifying-glass-bold shrink-0 size-4 @[160px]:hidden @[200px]:block" />
        <span className="hidden @[160px]:block">Search</span>
      </LegacyIconButton>

      <div className="flex-grow-1" />

      {(lockedFileCount > 0 || targetedFileCount > 0) && (
        <LegacyIconButton
          className={classNames('text-xs flex items-center relative px-1 @[224px]:px-1.5 gap-1', {
            'text-bolt-elements-textPrimary': tab === 'focused',
          })}
          onClick={() => onViewSelect('focused')}
        >
          {lockedFileCount > 0 && targetedFileCount > 0 ? (
            <>
              <span className="@[224px]:block hidden">{targetedFileCount}</span>
              <span className="i-ph:crosshair size-4" />
              <span className="@[224px]:block hidden">{lockedFileCount}</span>
              <span className="i-ph:lock-simple size-4" />
            </>
          ) : targetedFileCount > 0 ? (
            <>
              <span className="@[224px]:block hidden">{targetedFileCount}</span>
              <span className="i-ph:crosshair size-4" />
            </>
          ) : lockedFileCount > 0 ? (
            <>
              <span className="@[224px]:block hidden">{lockedFileCount}</span>
              <span className="i-ph:lock-simple size-4" />
            </>
          ) : null}
        </LegacyIconButton>
      )}
    </PanelHeader>
  );
});

interface FocusedFilesHeaderProps {
  type: 'targeted' | 'locked' | 'none';
  onViewSelect: (view: FileTreeViewType) => void;
}

const FocusedFilesHeader = memo<FocusedFilesHeaderProps>(({ type, onViewSelect }) => {
  function onResetAll() {
    workbenchStore.resetAllLockedTargetedFiles();
    onViewSelect('files');
  }

  return (
    <PanelHeader className="pr-2 pl-3 mt-1 gap-0.5 border-b-none">
      <div
        className={classNames('size-4 text-bolt-elements-item-contentDefault mr-1', {
          'i-ph:lock-simple': type === 'locked',
          'i-ph:crosshair': type === 'targeted',
        })}
      />
      <div className="shrink-0">{type === 'locked' ? 'Locked' : 'Targeted'} files</div>
      <div className="flex-grow-1" />
      <LegacyIconButton className="text-xs flex items-center shrink-0 relative -my-0.5 px-1.5" onClick={onResetAll}>
        Reset All
      </LegacyIconButton>
    </PanelHeader>
  );
});

interface FocusedFilesListProps {
  files: FileMap;
  type: 'targeted' | 'locked' | 'none';
  selectedFile: string | undefined;
  clearFocused: (file: FileNode | FolderNode) => void;
  onFileSelect?: FileTreePanelProps['onFileSelect'];
}

const FocusedFilesList = memo<FocusedFilesListProps>(({ type, files, selectedFile, onFileSelect, clearFocused }) => {
  /**
   * The file list has only two types of depth 0 for files and folders
   * that are directly targeted or locked and 1 for all the other ones.
   */
  const fileList = useMemo(() => {
    const result: (FileNode | FolderNode)[] = [];

    for (const [fullPath, dirent] of Object.entries(files)) {
      if (!dirent) {
        continue;
      }

      const depth = (type === 'locked' && dirent.isLocked) || (type === 'targeted' && dirent.isTargeted) ? 0 : 1;
      const name = fullPath.slice(fullPath.lastIndexOf('/') + 1);

      if (depth !== 1 || dirent.type !== 'folder') {
        result.push({
          ...(dirent.type === 'file' ? { kind: dirent.type, dirent } : { kind: dirent.type, dirent }),
          id: result.length,
          depth,
          fullPath,
          name,
        });
      }
    }

    return result;
  }, [files, type]);

  const { toggleCollapseState, collapsedFolders, filteredFileList } = useCollapsedFolders(
    fileList,
    true,
    selectedFile,
    true,
  );

  function renderFileElement(file: FileNode | FolderNode) {
    const parentFolder = file.fullPath.slice(WORK_DIR.length + 1, file.fullPath.lastIndexOf('/'));

    const isSelected = selectedFile === file.fullPath;

    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <div className="truncate pr-2">{file.name}</div>
        </Tooltip.Trigger>
        {parentFolder && <div className="text-xs truncate flex-1 mr-2 opacity-50">{parentFolder}</div>}
        {!parentFolder && <div className="flex-grow" />}
        {file.depth === 0 && (
          <span
            onClick={() => clearFocused(file)}
            className={classNames('hidden group-hover:block i-ph:x-bold size-3.5', {
              'text-bolt-elements-item-contentDefault hover:text-bolt-elements-item-contentActive': !isSelected,
              'group-hover:opacity-70 hover:opacity-100!': isSelected,
            })}
          />
        )}
        <Tooltip.Portal>
          <Tooltip.Content hideArrow side="bottom" align="start">
            {file.fullPath}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  }

  return (
    <div className="text-sm overflow-y-auto">
      <div className="px-3 mb-2 text-sm text-bolt-elements-textTertiary">
        {type === 'locked'
          ? 'Bolt will not make any changes to the following files:'
          : 'Bolt will only make changes to the following files:'}
      </div>
      <Tooltip.Provider>
        <div>
          {filteredFileList.map((fileOrFolder) => (
            <FileOrFolder
              key={fileOrFolder.fullPath}
              node={fileOrFolder}
              onClick={() =>
                fileOrFolder.kind === 'file'
                  ? onFileSelect?.(fileOrFolder.fullPath)
                  : toggleCollapseState(fileOrFolder.fullPath)
              }
              selected={selectedFile === fileOrFolder.fullPath}
              collapsed={collapsedFolders.has(fileOrFolder.fullPath)}
              renderContent={renderFileElement}
            />
          ))}
        </div>
      </Tooltip.Provider>
    </div>
  );
});

interface IconElementProps extends HTMLAttributes<HTMLSpanElement> {
  isFileSelected: boolean;
  isFocused: boolean | undefined;
  isClickable: boolean | undefined;
}

const IconElement: FC<IconElementProps> = ({
  isFileSelected,
  isFocused,
  isClickable,
  className,
  onClick,
  ...props
}) => (
  <span
    className={classNames(
      'size-4 shrink-0 opacity-0',
      {
        'text-bolt-elements-item-contentDefault hover:text-bolt-elements-item-contentActive':
          !isFileSelected && isClickable,
        'group-hover:opacity-70 hover:opacity-100!': isFileSelected,
        'group-hover:opacity-100': !isFileSelected,
        'not-hover-opacity-100': !isFileSelected && isFocused,
        'not-hover-opacity-70': isFileSelected && isFocused,
      },
      className,
    )}
    onClick={isClickable ? onClick : undefined}
    {...props}
  />
);
